import dayjs from 'dayjs'
import { RRule } from 'rrule'
import { isSameTimezone, SERVER_DATE_TIME_FORMAT, timezoneCity } from '@/components/desk/utils'

export const OFFICE_OPENS_HOUR = 8
export const SEARCH_WINDOW_HOURS = 8
const STEP_MINUTES = 30

// Round up to the next AppTimeSelect step, so the value is one the picker offers.
const ceilToStep = (moment) => {
  const rounded = moment.second(0).millisecond(0)
  const rest = rounded.minute() % STEP_MINUTES
  return rest === 0 ? rounded : rounded.add(STEP_MINUTES - rest, 'minute')
}

/**
 * The last minute of the day a moment falls on.
 *
 * The Bookings list is built on this: its pickers name whole days, and since/until are
 * half-open, so a To at midnight would ask for none of the day the user picked. The
 * Free Desks window only borrows it as a ceiling - there the user names the hour.
 */
export const endOfDay = (moment) => moment.hour(23).minute(59).second(0).millisecond(0)

/**
 * Default "find me a desk" window.
 *
 * Opening at a fixed 08:00 puts the window in the past for most of the working day,
 * and AppDateSelect rightly flags that - the filter would open already invalid.
 * Start at the office hour while it is still ahead, otherwise at the next step from now.
 */
export const defaultSearchWindow = (now = dayjs()) => {
  const opensAt = now.hour(OFFICE_OPENS_HOUR).minute(0).second(0).millisecond(0)
  const from = opensAt.isAfter(now) ? opensAt : ceilToStep(now)
  const to = from.add(SEARCH_WINDOW_HOURS, 'hour')
  const ceiling = endOfDay(from)

  return { from, to: to.isAfter(ceiling) ? ceiling : to }
}

// --- arriving from another screen --------------------------------------------

/**
 * The filters a screen was opened with.
 *
 * The /home cards link into the bookings screens with a period and a floor or two, so both
 * tabs have to be able to start on something other than their defaults. Everything here
 * arrives as text: an id is a string, and a single value arrives bare rather than as an
 * array of one. A value that cannot be read is dropped rather than guessed at - the screen
 * then opens on its own defaults, which is what a bare link does anyway.
 */
export const filtersFromQuery = (query = {}) => {
  const ids = (value) =>
    []
      .concat(value === undefined ? [] : value)
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)

  const moment = (value) => {
    if (typeof value !== 'string' || !value) return null
    const parsed = dayjs(value, SERVER_DATE_TIME_FORMAT)
    return parsed.isValid() ? parsed : null
  }

  return {
    floors: ids(query.floor),
    employees: ids(query.employee),
    since: moment(query.since),
    until: moment(query.until)
  }
}

/**
 * The filters on screen, written back as the address filtersFromQuery reads.
 *
 * Only what the reader chose goes in: a default window is today's, and written into a link
 * it would open tomorrow on yesterday's dates. A period is written whole or not at all.
 */
export const filtersToQuery = ({ floors = [], employees = [], since = null, until = null }) => {
  const query = {}
  if (employees.length) query.employee = employees.map(String)
  if (floors.length) query.floor = floors.map(String)
  if (since && until) {
    query.since = since.format(SERVER_DATE_TIME_FORMAT)
    query.until = until.format(SERVER_DATE_TIME_FORMAT)
  }
  return query
}

// moved to the router helpers, where every screen that writes its address finds it
export { sameQuery } from '@/router/navigation'

// --- floors, and the clock a search reads its window on ----------------------

const WALL_CLOCK_FORMAT = 'YYYY-MM-DD HH:mm'

/**
 * Floor picker options, each tagged with the clock its floor keeps.
 *
 * The zone lives on the building, so `timezoneOf` walks floor -> building for it and
 * answers null for a floor whose building has not loaded (or was never given one).
 */
export const floorOptions = (floors, timezoneOf) =>
  floors.map((floor) => ({ id: floor.id, label: floor.name, timezone: timezoneOf(floor) || null }))

/**
 * The zones on a list, counted as a reader could actually tell them apart.
 *
 * Two identifiers showing the same offset right now are one clock on screen, so only the
 * first of them is kept - a workspace holding "Europe/Helsinki" and "Europe/Kyiv" offices
 * is not one the viewer has to choose between.
 */
export const distinctTimezones = (zones) => {
  const kept = []
  for (const zone of zones) {
    if (zone && !kept.some((seen) => isSameTimezone(seen, zone))) kept.push(zone)
  }
  return kept
}

/** The clocks a search would have to read its window on: the picked floors, or all of them. */
export const searchTimezones = (selected, all) =>
  distinctTimezones((selected.length ? selected : all).map((floor) => floor.timezone))

/** Of several zones, the one standing closest to `reference` on the clock right now. */
export const nearestTimezone = (zones, reference) => {
  const now = dayjs()
  const here = now.tz(reference).utcOffset()

  return zones.reduce((nearest, zone) => {
      const gap = Math.abs(now.tz(zone).utcOffset() - here)
      return gap < nearest.gap ? { zone, gap } : nearest
    }, { zone: null, gap: Infinity }).zone
}

/**
 * The floors the Free Desks filter opens on.
 *
 * An empty filter means every floor, which only poses a question while the workspace keeps
 * one clock - there it stays empty and nothing needs saying. A workspace spread over zones
 * would otherwise open on a window that means a different hour in each, so the screen
 * starts on the office nearest the viewer's own clock instead of on the ambiguity.
 */
export const defaultFloorSelection = (floors, reference = dayjs.tz.guess()) => {
  const zones = distinctTimezones(floors.map((floor) => floor.timezone))
  if (zones.length < 2) return []

  const zone = nearestTimezone(zones, reference)
  return floors.filter((floor) => isSameTimezone(floor.timezone, zone))
}

/** The same wall clock read in another zone: 09:00 here becomes 09:00 there. */
export const inTimezone = (moment, zone) =>
  dayjs.tz(moment.format(WALL_CLOCK_FORMAT), WALL_CLOCK_FORMAT, zone)

// --- /desk-event-grouped -----------------------------------------------------


export const KIND_RECURRING = 'recurring'
export const KIND_PERMANENT = 'permanent'
export const KIND_SINGLE = 'single'

// what a permanent booking carries in `end`: 'infinity' beats any timestamp in Postgres
// and arrives over the wire as that literal string
export const INFINITY = 'infinity'

const DATE_LABEL_FORMAT = 'MMM D, YYYY'
const TIME_LABEL_FORMAT = 'HH:mm'

/**
 * Filter state -> axios params for GET /api/v1/desk-event-grouped.
 * Empty pickers are left out entirely so the server sees no filter at all.
 */
export const bookingsQueryParams = ({ employees = [], floors = [], from, to, page = 1, perPage = 20 }) => {
  const filters = {}
  if (employees.length) filters.employee = employees.map((item) => item.id)
  if (floors.length) filters.floor = floors.map((item) => item.id)
  if (from && to) {
    filters.since = from.format(SERVER_DATE_TIME_FORMAT)
    filters.until = to.format(SERVER_DATE_TIME_FORMAT)
  }

  return { filters, page, 'per-page': perPage, sort: 'start' }
}

/**
 * Working hours of a single occurrence, read in the given zone (null = the viewer's own).
 *
 * start/end are absolute instants (timestamptz). A series row carries the whole span in
 * start/end, so its daily end comes from the series duration, not from the last occurrence.
 */
const rangeIn = (row, timezone) => {
  if (row.kind === KIND_PERMANENT) return null

  const read = (value) => (timezone ? dayjs(value).tz(timezone) : dayjs(value))
  const start = read(row.start)

  if (row.kind === KIND_RECURRING) {
    // sets predating duration_minutes carry no daily length, and the row's own end is the
    // end of the whole series - showing that as a time would read as a months-long booking
    return row.duration_minutes ? { start, end: start.add(row.duration_minutes, 'minute') } : null
  }

  return { start, end: read(row.end) }
}

/** The viewer's own clock - what the table shows by default */
export const bookingTimeRange = (row) => rangeIn(row, null)

/** The clock of the building the desk stands in */
export const bookingBuildingTimeRange = (row) => rangeIn(row, row.timezone)

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1)

// rrule counts weekdays from Monday, dayjs from Sunday
const RRULE_WEEKDAYS = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU]

/**
 * How far the desk's calendar day sits from the viewer's while the series runs.
 *
 * An offset can carry a wall clock across midnight but no further, so this is -1, 0 or +1.
 * Read at the first occurrence: every occurrence keeps the same local time of day at the
 * desk, so they all move alike - bar a DST change that shifts a near-midnight booking
 * across the boundary partway through the series.
 */
const weekdayShift = (row) => {
  if (!row.timezone || !row.start) return 0

  const instant = dayjs(row.start)
  const raw = instant.day() - instant.tz(row.timezone).day()
  // raw can be +-6 when the two land either side of the week's end; fold it back to +-1
  return ((raw + 8) % 7) - 1
}

/**
 * The recurrence spelled out, optionally moved into the viewer's week.
 *
 * BYDAY is stored as the desk's own weekdays: the server re-expresses DTSTART in the
 * building zone before generating occurrences (DeskEventSet::getRruleInBuildingTimezone),
 * so "MO" means a Monday there. Printed as-is it contradicted the time beside it, which
 * has always been converted - a Monday evening in Vancouver is already Tuesday in
 * Helsinki, and the rule has to say so.
 */
const recurrenceText = (row, shift = 0) => {
  if (!row.rrule) return 'Recurring'
  try {
    const options = RRule.parseString(row.rrule)
    // the end is spelled out beside this, formatted like every other date on the screen
    // and read on the right clock; toText() would otherwise repeat it in its own words,
    // off the raw UTC value
    delete options.until
    if (shift && options.byweekday) {
      options.byweekday = [].concat(options.byweekday).map((day) => {
        const index = typeof day === 'number' ? day : day.weekday
        return RRULE_WEEKDAYS[(index + shift + 7) % 7]
      })
    }
    return capitalize(new RRule(options).toText())
  } catch (e) {
    // a rule the client build cannot parse must not take the whole table down
    return 'Recurring'
  }
}

/** The end the rule itself names, if it names one. */
const recurrenceUntil = (row) => {
  if (!row.rrule) return null
  try {
    return RRule.parseString(row.rrule).until || null
  } catch (e) {
    return null
  }
}

/**
 * The recurrence, and the stretch of calendar it covers.
 *
 * The rule alone said when a booking repeats but never since when, which left a series
 * that started months ago reading exactly like one starting next week.
 *
 * The opening date is the first occurrence on record. The closing one comes from the
 * rule's own UNTIL and is left off when there is none: an open-ended series has no end to
 * report, and the last row in the table is only as far as the generator has run - a daemon
 * tops the occurrences up (DeskEventSet::appendEvents), so that date moves by itself.
 *
 * `zone` null reads everything on the viewer's clock, and the stored weekdays are moved
 * into their week to match; passing the desk's zone gives the rule as it was written.
 */
const recurrenceLabel = (row, zone = null) => {
  const rule = recurrenceText(row, zone ? 0 : weekdayShift(row))
  if (!row.start) return rule

  const read = (value) => (zone ? dayjs(value).tz(zone) : dayjs(value)).format(DATE_LABEL_FORMAT)
  const until = recurrenceUntil(row)

  return until ? `${rule}, ${read(row.start)} – ${read(until)}` : `${rule}, from ${read(row.start)}`
}

export const bookingDateLabel = (row) => {
  // the date has to agree with the time beside it, so it is read in the viewer's zone too
  const start = dayjs(row.start)

  if (row.kind === KIND_PERMANENT) return `Permanent since ${start.format(DATE_LABEL_FORMAT)}`
  if (row.kind === KIND_RECURRING) return recurrenceLabel(row)
  return start.format(DATE_LABEL_FORMAT)
}

/**
 * Whether the booking is over.
 *
 * A permanent assignment never is. A recurring row carries the span of the whole series in
 * start/end, so it counts as over only once its last occurrence has been and gone.
 */
export const isBookingPast = (row, now = dayjs()) => {
  if (row.kind === KIND_PERMANENT || !row.end || row.end === INFINITY) return false
  return dayjs(row.end).isBefore(now)
}

export const bookingBadge = (row) => {
  if (row.kind === KIND_RECURRING) return { text: 'Recurring', variant: 'outline-info', icon: 'ion-md-refresh' }
  if (row.kind === KIND_PERMANENT) return { text: 'Permanent', variant: 'outline-secondary', icon: null }
  return null
}

// desk.number is nullable, and a bare "(Floor 2)" tells the reader nothing about which
// desk it is - fall back to the id so the row stays identifiable
const deskLabel = (row) => {
  const name = row.desk_number || (row.desk_id ? `#${row.desk_id}` : null)
  if (!name) return 'Unknown'
  return row.floor_name ? `${name} (${row.floor_name})` : name
}

/**
 * The date the booking falls on where the desk stands, shown beneath the viewer's own when
 * the two disagree.
 *
 * An offset moves the clock, and often enough it takes the calendar with it: an evening
 * booking in Vancouver has already become the next morning in Helsinki. The time column
 * has always named the clock it means; the date beside it read as the viewer's without
 * ever saying so.
 *
 * A recurring row carries its rule rather than a date, so what is restated there is the
 * rule: the stored weekdays are the desk's own, and the primary label has already been
 * moved into the viewer's week.
 */
const floorDateLabel = (row) => {
  if (!row.timezone) return null

  const city = timezoneCity(row.timezone)

  if (row.kind === KIND_RECURRING) {
    const there = recurrenceLabel(row, row.timezone)
    return there === bookingDateLabel(row) ? null : `${there} ${city}`
  }

  const start = dayjs(row.start)
  const here = start.format(DATE_LABEL_FORMAT)
  const there = start.tz(row.timezone).format(DATE_LABEL_FORMAT)

  return here === there ? null : `${there} ${city}`
}

const formatRange = (range) =>
  range ? `${range.start.format(TIME_LABEL_FORMAT)}–${range.end.format(TIME_LABEL_FORMAT)}` : null

/**
 * The floor's own clock, shown beneath the viewer's. Dropped when it would read the same -
 * a building that shares the viewer's offset adds nothing.
 *
 * This used to wait until the page held more than one building zone, on the grounds that a
 * single zone made the viewer's clock unambiguous. It does not: a workspace with one office
 * read from another country is the ordinary case, and there the only clock on screen was
 * the wrong one. Whether the two differ is the question, and the comparison below is
 * already asking it.
 */
const floorTimeLabel = (row, primary) => {
  if (!row.timezone) return null

  const label = formatRange(bookingBuildingTimeRange(row))
  if (!label || label === primary) return null

  return `${label} ${timezoneCity(row.timezone)}`
}

/** API row -> everything the table renders, so the template stays free of date maths. */
export const toBookingRow = (row) => {
  const range = bookingTimeRange(row)
  const timeLabel = formatRange(range)

  return {
    key: row.key,
    kind: row.kind,
    employeeId: row.employee_id,
    employeeName: row.employee_name || 'Unknown',
    avatarId: row.avatar_id,
    deskId: row.desk_id,
    // the map addresses points, not desks: ?pointId= takes a raw point id
    pointId: row.point_id,
    deskLabel: deskLabel(row),
    // the same place in parts, for layouts that show building, floor and desk apart
    deskName: row.desk_number || (row.desk_id ? `#${row.desk_id}` : 'Unknown'),
    floorName: row.floor_name || null,
    buildingName: row.building_name || null,
    floorId: row.floor_id,
    dateLabel: bookingDateLabel(row),
    floorDateLabel: floorDateLabel(row),
    timeLabel,
    floorTimeLabel: floorTimeLabel(row, timeLabel),
    badge: bookingBadge(row),
    // read once, when the page is built - the table reloads on every filter change, and
    // nothing here turns on the difference of a few minutes
    isPast: isBookingPast(row),
    occurrences: row.occurrences
  }
}
